import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { fetchClientConsents, recordClientConsent } from "../services/clientService";
import { formatDateTime } from "../services/datetime";
import { can } from "../services/permissions";
import { labelFor } from "../services/enumLabels";

const PURPOSES = ["marketing", "transactional", "data_processing", "photography"];
const CHANNELS = ["any", "email", "sms", "whatsapp", "push", "phone"];
const SOURCES = ["portal", "walk_in", "phone", "web_form", "app", "import"];

const blank = {
  purpose: "marketing",
  channel: "any",
  granted: "yes",
  source: "portal",
  version: "",
  recordedAt: "",
  note: "",
};

/**
 * Consent receipts — what this person agreed to, when, and through which channel.
 *
 * ## Append-only, and the screen has to look like it
 *
 * There is no edit and no delete here because there is none in the API: a
 * receipt is evidence, and evidence you can edit is not evidence. Withdrawing
 * posts a *new* receipt saying `granted: false`. So this panel has exactly two
 * halves — where they stand now, and the history that established it — and the
 * only action is to add to the history.
 *
 * ## No receipt means no
 *
 * A purpose with nothing recorded against it is shown as "not given", not as
 * blank or unknown. Consent is given, never assumed, and a screen that leaves
 * the cell empty invites somebody to read it as permission.
 */
export default function ClientConsent({ clientId }) {
  const { t, i18n } = useTranslation();

  const [receipts, setReceipts] = useState([]);
  const [current, setCurrent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const user = getCurrentUser();
  const mayRecord = can(user, "clients.update");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { consents, current: standing } = await fetchClientConsents(clientId);
      setReceipts(consents);
      setCurrent(standing);
    } catch (err) {
      setError(apiErrorMessage(err, t("clients.consent.loadFailed")));
      setReceipts([]);
      setCurrent([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (changes) => setForm((c) => ({ ...c, ...changes }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setFieldErrors({});

    try {
      await recordClientConsent(clientId, { ...form, granted: form.granted === "yes" });
      setForm(blank);
      setFormOpen(false);
      await load();
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("clients.consent.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  /** The standing receipts for one purpose, whatever channel they name. */
  const standingFor = (purpose) => current.filter((entry) => entry.purpose === purpose);

  return (
    <div className="space-y-4">
      {/* Where they stand now */}
      <div className="card p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold">{t("clients.consent.standingTitle")}</h3>
            <p className="text-xs text-ink-2">{t("clients.consent.standingHint")}</p>
          </div>
          {mayRecord && (
            <button type="button" className="btn-secondary" onClick={() => setFormOpen((o) => !o)}>
              {formOpen ? t("common.cancel") : t("clients.consent.record")}
            </button>
          )}
        </div>

        {loading ? (
          <p className="py-6 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : (
          <ul className="divide-y divide-line">
            {PURPOSES.map((purpose) => {
              const standing = standingFor(purpose);

              return (
                <li key={purpose} className="flex flex-wrap items-center gap-3 py-2">
                  <span className="w-40 text-sm font-medium">
                    {labelFor("consent_purpose", purpose)}
                  </span>

                  {standing.length === 0 ? (
                    <span className="rounded-full bg-raised px-3 py-1 text-xs text-ink-2">
                      {t("clients.consent.notGiven")}
                    </span>
                  ) : (
                    <span className="flex flex-wrap gap-2">
                      {standing.map((entry) => (
                        <span
                          key={`${entry.purpose}-${entry.channel}`}
                          className={`rounded-full px-3 py-1 text-xs ${
                            entry.granted
                              ? "bg-accent-wash text-accent-text"
                              : "bg-danger-wash text-danger-text"
                          }`}
                          title={
                            entry.recorded_at
                              ? formatDateTime(entry.recorded_at, i18n.language)
                              : undefined
                          }
                        >
                          {labelFor("consent_channel", entry.channel)} ·{" "}
                          {entry.granted
                            ? t("clients.consent.granted")
                            : t("clients.consent.withdrawn")}
                        </span>
                      ))}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Adding to the history — the only write this panel has. */}
      {formOpen && mayRecord && (
        <form onSubmit={submit} className="card space-y-3 p-4">
          <h3 className="text-sm font-semibold">{t("clients.consent.recordTitle")}</h3>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="consent-purpose">
                {t("clients.consent.purpose")}
              </label>
              <select
                id="consent-purpose"
                className="field-input"
                value={form.purpose}
                onChange={(e) => update({ purpose: e.target.value })}
              >
                {PURPOSES.map((purpose) => (
                  <option key={purpose} value={purpose}>
                    {labelFor("consent_purpose", purpose)}
                  </option>
                ))}
              </select>
              {fieldErrors.purpose && <p className="field-error">{fieldErrors.purpose[0]}</p>}
            </div>

            <div>
              <label className="field-label" htmlFor="consent-channel">
                {t("clients.consent.channel")}
              </label>
              <select
                id="consent-channel"
                className="field-input"
                value={form.channel}
                onChange={(e) => update({ channel: e.target.value })}
              >
                {CHANNELS.map((channel) => (
                  <option key={channel} value={channel}>
                    {labelFor("consent_channel", channel)}
                  </option>
                ))}
              </select>
              {fieldErrors.channel && <p className="field-error">{fieldErrors.channel[0]}</p>}
            </div>

            <div>
              <label className="field-label" htmlFor="consent-granted">
                {t("clients.consent.decision")}
              </label>
              <select
                id="consent-granted"
                className="field-input"
                value={form.granted}
                onChange={(e) => update({ granted: e.target.value })}
              >
                <option value="yes">{t("clients.consent.granted")}</option>
                <option value="no">{t("clients.consent.withdrawn")}</option>
              </select>
            </div>

            <div>
              <label className="field-label" htmlFor="consent-source">
                {t("clients.consent.source")}
              </label>
              <select
                id="consent-source"
                className="field-input"
                value={form.source}
                onChange={(e) => update({ source: e.target.value })}
              >
                {SOURCES.map((source) => (
                  <option key={source} value={source}>
                    {labelFor("consent_source", source)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="field-label" htmlFor="consent-version">
                {t("clients.consent.version")}
              </label>
              <input
                id="consent-version"
                className="field-input"
                placeholder={t("clients.consent.versionHint")}
                value={form.version}
                onChange={(e) => update({ version: e.target.value })}
              />
              {fieldErrors.version && <p className="field-error">{fieldErrors.version[0]}</p>}
            </div>

            <div>
              <label className="field-label" htmlFor="consent-recorded-at">
                {t("clients.consent.recordedAt")}
              </label>
              <input
                id="consent-recorded-at"
                type="datetime-local"
                className="field-input"
                value={form.recordedAt}
                onChange={(e) => update({ recordedAt: e.target.value })}
              />
              {/* Paper consent collected last week is backdated; nothing may be
                  dated into the future, and the API refuses it if it is. */}
              <p className="mt-1 text-xs text-ink-2">{t("clients.consent.recordedAtHint")}</p>
              {fieldErrors.recorded_at && (
                <p className="field-error">{fieldErrors.recorded_at[0]}</p>
              )}
            </div>
          </div>

          <div>
            <label className="field-label" htmlFor="consent-note">
              {t("clients.consent.note")}
            </label>
            <input
              id="consent-note"
              className="field-input"
              value={form.note}
              onChange={(e) => update({ note: e.target.value })}
            />
          </div>

          {error && <p className="field-error">{error}</p>}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-secondary"
              disabled={saving}
              onClick={() => {
                setFormOpen(false);
                setForm(blank);
              }}
            >
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t("common.saving") : t("clients.consent.save")}
            </button>
          </div>
        </form>
      )}

      {!formOpen && error && <p className="field-error">{error}</p>}

      {/* The history. Withdrawals are receipts too, which is why they are here
          rather than being an absence. */}
      <div className="card overflow-hidden">
        <div className="border-b border-line px-4 py-3">
          <h3 className="text-sm font-semibold">{t("clients.consent.historyTitle")}</h3>
          <p className="text-xs text-ink-2">{t("clients.consent.historyHint")}</p>
        </div>

        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : receipts.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-ink-2">
            {t("clients.consent.historyEmpty")}
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {receipts.map((receipt) => (
              <li key={receipt.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
                <span
                  className={`rounded-full px-3 py-1 text-xs ${
                    receipt.granted ? "bg-accent-wash text-accent-text" : "bg-danger-wash text-danger-text"
                  }`}
                >
                  {receipt.granted
                    ? t("clients.consent.granted")
                    : t("clients.consent.withdrawn")}
                </span>

                <div className="min-w-[200px] flex-1">
                  <p className="text-sm">
                    {labelFor("consent_purpose", receipt.purpose)} ·{" "}
                    {labelFor("consent_channel", receipt.channel)}
                  </p>
                  <p className="text-xs text-ink-2">
                    {t("clients.consent.receiptLine", {
                      source: labelFor("consent_source", receipt.source),
                      by: receipt.recorded_by_name ?? t("clients.notes.unknownAuthor"),
                      when: formatDateTime(receipt.recorded_at, i18n.language),
                    })}
                    {receipt.version ? ` · ${receipt.version}` : ""}
                  </p>
                  {receipt.note && <p className="mt-1 text-xs text-ink-2">{receipt.note}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
